import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

/**
 * AppContext - Global state management for app and workflow data
 *
 * Features:
 * - Caches selected app ID and name
 * - Caches workflows for current app
 * - Provides helper to lookup workflow name from ID
 * - Populated by AppDetails page when Overview tab loads
 * - Used by ConsoleLayout to show workflow in left nav
 *
 * Mirrors EnvironmentContext pattern
 */

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [selectedAppName, setSelectedAppName] = useState(null);
  const [workflows, setWorkflows] = useState([]);

  // Update app data (called by AppDetails after fetching)
  const updateApp = useCallback((appId, appName, workflowsList) => {
    setSelectedAppId(appId);
    setSelectedAppName(appName);
    setWorkflows(workflowsList || []);
  }, []);

  // Clear app data (when navigating away from app context)
  const clearApp = useCallback(() => {
    setSelectedAppId(null);
    setSelectedAppName(null);
    setWorkflows([]);
  }, []);

  // Helper: Get workflow name from workflow ID
  const getWorkflowName = useCallback((workflowId) => {
    if (!workflowId || !workflows.length) return null;

    const workflow = workflows.find(w => w.workflow_id === workflowId);
    return workflow?.workflow_name || workflow?.workflow_id || null;
  }, [workflows]);

  // Helper: Get workflow by ID
  const getWorkflow = useCallback((workflowId) => {
    if (!workflowId || !workflows.length) return null;
    return workflows.find(w => w.workflow_id === workflowId) || null;
  }, [workflows]);

  const value = useMemo(() => ({
    selectedAppId,
    selectedAppName,
    workflows,
    updateApp,
    clearApp,
    getWorkflowName,
    getWorkflow
  }), [selectedAppId, selectedAppName, workflows, updateApp, clearApp, getWorkflowName, getWorkflow]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook for easy access to app context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
